import React, { useState, useEffect } from 'react';
import { AlertCircle, BookOpen, Briefcase, Calendar, CheckCircle, Clock, DollarSign, FileText, Gavel, MapPin, Plus, Sparkles, Trash2, Users, X } from 'lucide-react';
import { createCase } from '../services/caseService';
import { createClient, getClients, getClientPayments } from '../services/clientService';
import { getTimeEntries } from '../services/billingService';
import { getEnhancedTasks } from '../services/enhancedTaskService';
import { getCourtDates } from '../services/courtReminderService';
import { getWeekEvents } from '../services/calendarService';
import { logAuditEvent, AUDIT_ACTIONS, AUDIT_CATEGORIES } from '../services/auditService';
import { sanitizeObject } from '../services/sanitize';
import { toast } from '../utils/toast';
import { checkLimit } from '../utils/subscription';
import { PARTY_ROLES, CASE_PLAYBOOKS } from '../utils/playbooks';
import { isOverdue, formatAccraDate, compareDueDatesForSort } from '../utils/firmassistTime';
import DashboardOnboardingChecklist from './DashboardOnboardingChecklist';

// Mini component: Court dates on dashboard (checks BOTH tasks and courtDates collections)
const DashboardCourtDates = ({ firmId, setActiveTab, userId, dataVersion, upcomingTasks }) => {
  const [dates, setDates] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  useEffect(() => {
    if (!userId && !firmId) return;
    const today = new Date().toISOString().split('T')[0];
    const allCourtDates = [];

    // Source 1: Tasks with eventType=court_date (from upcomingTasks prop or fresh query)
    const taskPromise = upcomingTasks
      ? Promise.resolve(upcomingTasks)
      : getEnhancedTasks(userId, {}, firmId).then(r => r.success ? r.tasks : []);

    // Source 2: courtDates collection (Court Dates Schedule)
    const courtDatesPromise = firmId
      ? getCourtDates(firmId, { upcoming: true }).then(r => r.success ? r.dates : []).catch(() => [])
      : Promise.resolve([]);

    Promise.all([taskPromise, courtDatesPromise]).then(([tasks, courtDatesDocs]) => {
      // Add court_date tasks
      (tasks || []).forEach(t => {
        if (t.eventType === 'court_date' && t.status !== 'completed' && t.dueDate >= today) {
          allCourtDates.push({ ...t, _source: 'tasks' });
        }
      });

      // Add courtDates collection entries
      (courtDatesDocs || []).forEach(cd => {
        if (cd.date >= today && cd.status !== 'completed' && cd.status !== 'cancelled') {
          // Check for duplicates (same date + same case name)
          const isDuplicate = allCourtDates.some(existing =>
            existing.dueDate === cd.date && existing.caseName === cd.caseName
          );
          if (!isDuplicate) {
            allCourtDates.push({
              id: cd.id,
              title: cd.caseName || cd.type || 'Court Date',
              dueDate: cd.date,
              dueTime: cd.time || '',
              court: cd.court || '',
              location: cd.court || '',
              judge: cd.judge || '',
              hearingType: cd.type || 'hearing',
              caseName: cd.caseName || '',
              caseNumber: cd.caseNumber || '',
              eventType: 'court_date',
              _source: 'courtDates'
            });
          }
        }
      });

      // Sort by date and take top 4
      allCourtDates.sort((a, b) => compareDueDatesForSort(a.dueDate, b.dueDate));
      setDates(allCourtDates.slice(0, 5));
    });
  }, [userId, firmId, dataVersion, upcomingTasks]);
  return (
    <div className="dashboard-card">
      <div className="card-header">
        <h3>⚖️ Court Dates</h3>
        <button className="btn-ghost" onClick={() => setActiveTab && setActiveTab('tasks')}>View All</button>
      </div>
      <div className="task-list">
        {dates.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#a8adb7' }}><Gavel size={32} style={{ opacity: 0.4 }} /><p style={{ margin: '0.5rem 0 0' }}>No upcoming court dates</p></div>
        ) : dates.map(cd => {
          const diff = Math.ceil((new Date(cd.dueDate + 'T12:00:00') - new Date()) / (1000*60*60*24));
          const color = diff <= 1 ? '#ef4444' : diff <= 3 ? '#f59e0b' : '#10b981';
          return (
            <div key={cd.id} className="task-item" onClick={() => setSelectedCourt(cd)} style={{ cursor: 'pointer', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,168,83,0.06)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div className="task-details">
                <div className="task-title" style={{ color: diff <= 1 ? '#ef4444' : '#e8eaed' }}>{diff <= 1 ? '🔴 ' : ''}{cd.title || cd.caseName}</div>
                <div className="task-meta"><span className="task-case">{cd.court || cd.location || ''} • {cd.dueTime || '9:00 AM'}</span></div>
              </div>
              <div className="task-due" style={{ color, fontWeight: diff <= 3 ? 600 : 400 }}>
                {diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : new Date(cd.dueDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Court Date Detail Modal */}
      {selectedCourt && (
        <div onMouseDown={e => { if (e.target === e.currentTarget) setSelectedCourt(null); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#0f1219', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', maxWidth: '480px', width: '100%', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
                <h3 style={{ color: '#e8eaed', margin: 0, fontSize: '1.05rem', fontFamily: "'Playfair Display', Georgia, serif" }}>Court Date Details</h3>
              </div>
              <button onClick={() => setSelectedCourt(null)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '0.25rem' }}><X size={20} /></button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem' }}>
              <h2 style={{ color: '#e8eaed', fontSize: '1.15rem', fontWeight: 600, marginBottom: '1.25rem' }}>
                ⚖️ {selectedCourt.title || selectedCourt.caseName || 'Court Hearing'}
              </h2>

              <div style={{ display: 'grid', gap: '0.85rem' }}>
                {/* Date & Time */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Calendar size={18} style={{ color: '#ef4444', flexShrink: 0 }} />
                  <div>
                    <div style={{ color: '#e8eaed', fontSize: '0.92rem', fontWeight: 500 }}>
                      {new Date(selectedCourt.dueDate + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                    {selectedCourt.dueTime && <div style={{ color: '#8b8fa3', fontSize: '0.82rem' }}>at {selectedCourt.dueTime}</div>}
                  </div>
                </div>

                {/* Court */}
                {(selectedCourt.court || selectedCourt.location) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <MapPin size={18} style={{ color: '#d4a853', flexShrink: 0 }} />
                    <div>
                      {selectedCourt.court && <div style={{ color: '#e8eaed', fontSize: '0.92rem' }}>{selectedCourt.court}</div>}
                      {selectedCourt.location && selectedCourt.location !== selectedCourt.court && <div style={{ color: '#8b8fa3', fontSize: '0.82rem' }}>{selectedCourt.location}</div>}
                    </div>
                  </div>
                )}

                {/* Judge */}
                {selectedCourt.judge && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Gavel size={18} style={{ color: '#d4a853', flexShrink: 0 }} />
                    <div style={{ color: '#e8eaed', fontSize: '0.92rem' }}>Justice {selectedCourt.judge}</div>
                  </div>
                )}

                {/* Hearing Type */}
                {selectedCourt.hearingType && selectedCourt.hearingType !== 'hearing' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <FileText size={18} style={{ color: '#8b8fa3', flexShrink: 0 }} />
                    <div style={{ color: '#e8eaed', fontSize: '0.92rem' }}>{selectedCourt.hearingType.charAt(0).toUpperCase() + selectedCourt.hearingType.slice(1)}</div>
                  </div>
                )}

                {/* Case */}
                {selectedCourt.caseName && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Briefcase size={18} style={{ color: '#8b8fa3', flexShrink: 0 }} />
                    <div style={{ color: '#e8eaed', fontSize: '0.92rem' }}>{selectedCourt.caseName}</div>
                  </div>
                )}

                {/* Priority */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <AlertCircle size={18} style={{ color: selectedCourt.priority === 'high' ? '#ef4444' : selectedCourt.priority === 'medium' ? '#f59e0b' : '#6b7280', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.82rem', padding: '0.2rem 0.6rem', borderRadius: '6px', background: selectedCourt.priority === 'high' ? 'rgba(239,68,68,0.12)' : selectedCourt.priority === 'medium' ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.05)', color: selectedCourt.priority === 'high' ? '#ef4444' : selectedCourt.priority === 'medium' ? '#f59e0b' : '#6b7280', fontWeight: 600, textTransform: 'capitalize' }}>{selectedCourt.priority} priority</span>
                </div>

                {/* Notes */}
                {selectedCourt.notes && (
                  <div style={{ marginTop: '0.25rem', padding: '0.75rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px' }}>
                    <div style={{ color: '#6b7280', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>Notes</div>
                    <div style={{ color: '#8b8fa3', fontSize: '0.85rem', lineHeight: 1.6 }}>{selectedCourt.notes}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: '0.5rem' }}>
              {selectedCourt.caseId && (
                <button onClick={() => { setSelectedCourt(null); if (setActiveTab) setActiveTab('cases'); window.dispatchEvent(new CustomEvent('firmassist-select-case', { detail: { caseId: selectedCourt.caseId, caseName: selectedCourt.caseName } })); }} style={{ flex: 1, padding: '0.65rem', background: 'rgba(212,168,83,0.08)', border: '1px solid rgba(212,168,83,0.25)', borderRadius: '10px', color: '#d4a853', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}><Briefcase size={15} /> View Case</button>
              )}
              <button onClick={() => { setSelectedCourt(null); if (setActiveTab) setActiveTab('tasks'); }} style={{ flex: 1, padding: '0.65rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: '#8b8fa3', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}><Calendar size={15} /> Open Calendar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Dashboard = ({ recentCases, upcomingTasks, userId, setActiveTab, user, firmId, onCaseCreated, dataVersion, userRole, userPermissions, planFeatures, activeTab, onNavigateToPersonalNotificationSettings }) => {
  const [clients, setClients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);

  // ── New Case Modal State ──
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [newCaseForm, setNewCaseForm] = useState({ name: '', client: '', type: 'Criminal Defense', status: 'Active', priority: 'medium', caseNumber: '', description: '', casePlaybook: '', parties: [] });
  const [newCaseLoading, setNewCaseLoading] = useState(false);
  const [newCaseError, setNewCaseError] = useState('');
  const [dashParties, setDashParties] = useState([]);
  const dashCaseTypes = ['Criminal Defense', 'Corporate Law', 'Civil Litigation', 'Family Law', 'Land & Property', 'Estate Planning', 'Labor & Employment', 'Commercial Law', 'Constitutional Law', 'Banking & Finance', 'Tax Law', 'Chieftaincy', 'Environmental Law', 'Maritime Law', 'Immigration', 'Intellectual Property', 'Insurance', 'Mining & Energy', 'Human Rights', 'Election Petition', 'Rent & Housing', 'Debt Recovery', 'Other'];

  const handleNewCaseSubmit = async (e) => {
    e.preventDefault();
    setNewCaseError('');
    setNewCaseLoading(true);
    try {
      const allowed = await checkLimit(firmId, 'case', toast);
      if (!allowed) { setNewCaseLoading(false); setNewCaseError('Case limit reached on your plan.'); return; }
      const ourClient = dashParties.find(p => p.isOurClient)?.name || newCaseForm.client;
      const caseData = { ...newCaseForm, client: ourClient, clientName: ourClient, userId, firmId, caseNumber: newCaseForm.caseNumber || ('CASE-' + new Date().getFullYear() + '-' + String(Date.now()).slice(-5)), parties: dashParties, casePlaybook: newCaseForm.casePlaybook || '' };
      const result = await createCase(userId, sanitizeObject(caseData), firmId);
      if (result.success) {
        // Auto-create client from parties or client field
        const clientName = ourClient || newCaseForm.client;
        if (clientName?.trim()) {
          try {
            const existing = await getClients(userId, {}, firmId);
            const exists = existing.success && existing.clients.some(c => c.name?.toLowerCase().trim() === clientName.toLowerCase().trim());
            if (!exists) await createClient(userId, { name: clientName.trim(), status: 'active', notes: 'Auto-created from case: ' + newCaseForm.name }, firmId);
          } catch(e) {}
        }
        setShowNewCaseModal(false);
        setNewCaseForm({ name: '', client: '', type: 'Criminal Defense', status: 'Active', priority: 'medium', caseNumber: '', description: '', casePlaybook: '', parties: [] });
        setDashParties([]);
        if (onCaseCreated) onCaseCreated();
        // Also reload Dashboard's own stats (clients, payments) so the new client shows
        loadDashboardData();
        logAuditEvent(firmId, { userId, userName: user?.displayName || user?.email, action: AUDIT_ACTIONS.CREATED, category: AUDIT_CATEGORIES.CASE, details: `Created case "${newCaseForm.name}" for ${newCaseForm.client}`, entityId: result.id, entityName: newCaseForm.name }).catch(e => console.error('[AUDIT FAILED]', e.message));
      } else { setNewCaseError(result.error || 'Failed to create case'); }
    } catch(e) { setNewCaseError('An error occurred'); }
    finally { setNewCaseLoading(false); }
  };

  useEffect(() => { if (userId) loadDashboardData(); }, [userId, dataVersion]);

  const loadDashboardData = async () => {
    setStatsLoading(true);
    const [clientsRes, paymentsRes, timeRes] = await Promise.all([
      getClients(userId, {}, firmId),
      getClientPayments(userId, null, firmId),
      getTimeEntries(userId, {}, firmId)
    ]);
    if (clientsRes.success) setClients(clientsRes.clients);
    if (paymentsRes.success) setPayments(paymentsRes.payments);
    if (timeRes.success) setTimeEntries(timeRes.entries);
    setStatsLoading(false);
  };

  // ---- Computed stats ----
  const activeCases = recentCases.filter(c => c.status === 'Active' || c.status === 'active').length;
  const totalRevenue = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const billableHours = timeEntries.filter(t => t.billable !== false).reduce((s, t) => s + (parseFloat(t.hours) || 0), 0);
  const activeClients = clients.filter(c => c.status === 'active').length;
  const totalOutstanding = Math.max(clients.reduce((s, c) => s + (parseFloat(c.totalBilled) || 0), 0) - totalRevenue, 0);

  // Practice area stats from real cases
  const practiceAreas = {};
  recentCases.forEach(c => { const area = c.type || c.practiceArea || 'Other'; practiceAreas[area] = (practiceAreas[area] || 0) + 1; });
  const totalCaseCount = recentCases.length || 1;
  const practiceAreaStats = Object.entries(practiceAreas).sort((a, b) => b[1] - a[1]).map(([area, count]) => ({ area, cases: count, percentage: Math.round((count / totalCaseCount) * 100) }));

  // Overdue tasks — Africa/Accra business-time semantics (utils/firmassistTime.js)
  const overdueTasks = upcomingTasks.filter(t => !t.completed && isOverdue({ dueDate: t.dueDate || t.due, dueTime: t.dueTime, status: t.status }));

  // Greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';

  const cardBg = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' };
  const paColors = ['#d4a853', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4'];

  return (
    <div className="dashboard-content">
      <div className="dashboard-header">
        <div>
          <h1 className="page-title">{greeting}, {user?.displayName?.split(' ')[0] || 'Counsel'} 👋</h1>
          <p className="page-subtitle">Here's what's happening with your practice today</p>
        </div>
        <button className="btn-primary" onClick={() => setShowNewCaseModal(true)}>
          <Plus size={18} /> New Case
        </button>
      </div>

      <DashboardOnboardingChecklist
        userId={userId}
        firmId={firmId}
        userRole={userRole}
        userPermissions={userPermissions}
        planFeatures={planFeatures}
        recentCases={recentCases}
        upcomingTasks={upcomingTasks}
        setActiveTab={setActiveTab}
        onOpenNewCase={() => setShowNewCaseModal(true)}
        activeTab={activeTab}
        onNavigateToPersonalNotificationSettings={onNavigateToPersonalNotificationSettings}
      />

      {/* REAL STATS */}
      <div className="stats-grid">
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setActiveTab && setActiveTab('cases')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #d4a853, #b8862d)' }}><Briefcase size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{statsLoading ? '...' : activeCases}</div>
            <div className="stat-label">Active Cases</div>
            <div style={{ color: '#a8adb7', fontSize: '0.8rem' }}>{recentCases.length} total</div>
          </div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setActiveTab && setActiveTab('billing')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}><Clock size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{statsLoading ? '...' : billableHours.toFixed(1)}h</div>
            <div className="stat-label">Billable Hours</div>
            <div style={{ color: '#a8adb7', fontSize: '0.8rem' }}>{timeEntries.length} entries</div>
          </div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setActiveTab && setActiveTab('clients')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4facfe, #00f2fe)' }}><DollarSign size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">GH₵{statsLoading ? '...' : totalRevenue.toLocaleString()}</div>
            <div className="stat-label">Revenue Collected</div>
            {totalOutstanding > 0 && <div style={{ color: '#ef4444', fontSize: '0.8rem' }}>GH₵{totalOutstanding.toLocaleString()} outstanding</div>}
          </div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setActiveTab && setActiveTab('clients')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fa709a, #fee140)' }}><Users size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{statsLoading ? '...' : activeClients}</div>
            <div className="stat-label">Active Clients</div>
            <div style={{ color: '#a8adb7', fontSize: '0.8rem' }}>{clients.length} total</div>
          </div>
        </div>
      </div>

      {/* OVERDUE TASKS ALERT */}
      {overdueTasks.length > 0 && (
        <div style={{ ...cardBg, padding: '1.25rem', marginBottom: '1.5rem', background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.3)', cursor: 'pointer' }} onClick={() => setActiveTab && setActiveTab('tasks')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <AlertCircle size={22} style={{ color: '#ef4444' }} />
            <div><span style={{ color: '#ef4444', fontWeight: 700 }}>{overdueTasks.length} overdue task{overdueTasks.length > 1 ? 's' : ''}</span><span style={{ color: '#a8adb7', marginLeft: '0.5rem', fontSize: '0.9rem' }}>— click to review</span></div>
          </div>
        </div>
      )}

{/* ════════════════════════════════════════════════════════════════ */}
      {/* TODAY'S AGENDA — Morning Briefing Timeline                       */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {(() => {
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const tomorrowStr = new Date(today.getTime() + 86400000).toISOString().split('T')[0];

        // Collect today's events
        const todayItems = [];

        // Court dates
        upcomingTasks.filter(t => t.eventType === 'court_date' && t.dueDate === todayStr && t.status !== 'completed').forEach(t => {
          const courtInfo = [t.court, t.judge ? 'Justice ' + t.judge : '', t.hearingType && t.hearingType !== 'hearing' ? t.hearingType.charAt(0).toUpperCase() + t.hearingType.slice(1) : ''].filter(Boolean).join(' · ');
          todayItems.push({ time: t.dueTime || '09:00', title: t.title || 'Court Hearing', subtitle: (t.caseName || '') + (courtInfo ? ' — ' + courtInfo : ''), icon: '⚖️', color: '#ef4444', type: 'court' });
        });

        // Tasks due today
        upcomingTasks.filter(t => (t.dueDate === todayStr || t.due === todayStr) && t.status !== 'completed' && t.eventType !== 'court_date').forEach(t => {
          todayItems.push({ time: t.dueTime || '—', title: t.title, subtitle: t.caseName || t.case || '', icon: '📋', color: '#d4a853', type: 'task' });
        });

        // Deadlines from cases
        recentCases.filter(c => c.deadline === todayStr && c.status !== 'Closed' && c.status !== 'closed').forEach(c => {
          todayItems.push({ time: '—', title: 'Case Deadline: ' + c.name, subtitle: c.client || c.clientName || '', icon: '🔴', color: '#ef4444', type: 'deadline' });
        });

        // Sort by time
        todayItems.sort((a, b) => (a.time || '').localeCompare(b.time || ''));

        // Tomorrow preview
        const tomorrowItems = [];
        upcomingTasks.filter(t => (t.dueDate === tomorrowStr || t.due === tomorrowStr) && t.status !== 'completed').forEach(t => {
          tomorrowItems.push({ title: t.title, type: t.eventType === 'court_date' ? 'court' : 'task' });
        });

        return (
          <div style={{ ...cardBg, padding: '1.5rem', marginBottom: '1.5rem', background: 'linear-gradient(135deg, rgba(212,168,83,0.05), rgba(184,134,45,0.05))' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div>
                <h3 style={{ color: '#e8eaed', margin: 0, marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  📅 Today's Agenda
                </h3>
                <p style={{ color: '#a8adb7', fontSize: '0.85rem', margin: 0 }}>
                  {today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ background: 'rgba(212,168,83,0.15)', color: '#d4a853', padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600 }}>
                  {todayItems.length} item{todayItems.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {todayItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#a8adb7' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>☀️</div>
                <h3 style={{ color: '#10b981', marginBottom: '0.25rem', fontSize: '1rem' }}>Clear schedule today!</h3>
                <p style={{ fontSize: '0.85rem' }}>No court dates, tasks, or deadlines due today.</p>
              </div>
            ) : (
              <div style={{ position: 'relative', paddingLeft: '2rem' }}>
                {/* Timeline line */}
                <div style={{ position: 'absolute', left: '0.7rem', top: '0.5rem', bottom: '0.5rem', width: '2px', background: 'rgba(212,168,83,0.2)' }} />

                {todayItems.map((item, i) => (
                  <div key={i} style={{ position: 'relative', marginBottom: i < todayItems.length - 1 ? '1rem' : 0, display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                    {/* Timeline dot */}
                    <div style={{
                      position: 'absolute', left: '-1.65rem', top: '0.35rem',
                      width: '12px', height: '12px', borderRadius: '50%',
                      background: item.color, border: '2px solid #0f1219',
                      boxShadow: '0 0 0 2px ' + item.color + '44'
                    }} />

                    {/* Time */}
                    <div style={{ minWidth: '55px', color: item.type === 'court' ? '#ef4444' : '#a8adb7', fontSize: '0.85rem', fontWeight: 600, paddingTop: '0.15rem' }}>
                      {item.time !== '—' ? item.time : ''}
                    </div>

                    {/* Content */}
                    <div style={{
                      flex: 1, background: item.type === 'court' ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.03)',
                      border: '1px solid ' + (item.type === 'court' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.06)'),
                      borderRadius: '10px', padding: '0.75rem 1rem',
                      borderLeft: '3px solid ' + item.color
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{item.icon}</span>
                        <span style={{ color: '#e8eaed', fontWeight: 600, fontSize: '0.9rem' }}>{item.title}</span>
                      </div>
                      {item.subtitle && <div style={{ color: '#a8adb7', fontSize: '0.8rem', marginTop: '0.2rem', marginLeft: '1.6rem' }}>{item.subtitle}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Tomorrow preview */}
            {tomorrowItems.length > 0 && (
              <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ color: '#a8adb7', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>📌 TOMORROW</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {tomorrowItems.slice(0, 4).map((t, i) => (
                    <span key={i} style={{
                      background: t.type === 'court' ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.05)',
                      border: '1px solid ' + (t.type === 'court' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.08)'),
                      borderRadius: '8px', padding: '0.35rem 0.65rem', color: t.type === 'court' ? '#ef4444' : '#e8eaed',
                      fontSize: '0.8rem'
                    }}>
                      {t.type === 'court' ? '⚖️' : '📋'} {t.title}
                    </span>
                  ))}
                  {tomorrowItems.length > 4 && <span style={{ color: '#d4a853', fontSize: '0.8rem', padding: '0.35rem' }}>+{tomorrowItems.length - 4} more</span>}
                </div>
              </div>
            )}
          </div>
        );
      })()}


      {/* WEEK DEADLINES */}
      <div className="widget-card" style={{gridColumn: '1 / -1', background: 'linear-gradient(135deg, rgba(212,168,83,0.1), rgba(184,134,45,0.1))', border: '1px solid rgba(212,168,83,0.3)', marginBottom: '2rem', borderRadius: '16px', padding: '1.5rem'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem'}}>
          <div>
            <h3 style={{color: '#e8eaed', margin: 0, marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}><Calendar size={22} style={{color: '#d4a853'}} /> This Week's Deadlines</h3>
            <p style={{color: '#a8adb7', fontSize: '0.85rem', margin: 0}}>Cases and tasks due in the next 7 days</p>
          </div>
        </div>
        {(() => {
          const weekEvents = getWeekEvents(recentCases, upcomingTasks);
          if (weekEvents.length === 0) return <div style={{textAlign: 'center', padding: '2rem', color: '#a8adb7'}}><CheckCircle size={48} style={{opacity: 0.5, margin: '0 auto 0.75rem', color: '#10b981'}} /><h3 style={{color: '#10b981', marginBottom: '0.25rem'}}>All caught up!</h3><p>No upcoming deadlines this week</p></div>;
          return (
            <div style={{display: 'grid', gap: '0.5rem'}}>
              {weekEvents.slice(0, 6).map(event => (
                <div key={event.id} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1}}>
                    {event.type === 'case' ? <Briefcase size={16} style={{color: '#d4a853'}} /> : <CheckCircle size={16} style={{color: '#10b981'}} />}
                    <div>
                      <div style={{color: '#e8eaed', fontWeight: 500}}>
                        {event.caseName || event.title}
                      </div>
                      <div style={{color: '#a8adb7', fontSize: '0.8rem'}}>
                        {event.caseName && event.title && event.caseName !== event.title && (
                          <span style={{color: '#d4a853', fontWeight: 600, marginRight: '0.35rem'}}>{event.title} ·</span>
                        )}
                        {formatAccraDate(event.date, { weekday: 'short', month: 'short', day: 'numeric' }, 'en-US')}
                      </div>
                    </div>
                  </div>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: event.priority === 'high' ? '#ef4444' : event.priority === 'medium' ? '#fbbf24' : '#3b82f6', display: 'inline-block', flexShrink: 0 }} title={event.priority} />
                </div>
              ))}
              {weekEvents.length > 6 && <div style={{ textAlign: 'center', padding: '0.5rem', color: '#d4a853', cursor: 'pointer', fontSize: '0.9rem' }} onClick={() => setActiveTab && setActiveTab('tasks')}>+{weekEvents.length - 6} more — View All →</div>}
            </div>
          );
        })()}
      </div>

      <div className="dashboard-grid">
        {/* Upcoming Tasks */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Upcoming Tasks</h3>
            <button className="btn-ghost" onClick={() => setActiveTab && setActiveTab('tasks')}>View All</button>
          </div>
          <div className="task-list">
            {(() => {
              const pendingTasks = upcomingTasks.filter(t => t.status !== 'completed' && !t.completed);
              pendingTasks.sort((a, b) => compareDueDatesForSort(a.dueDate || a.due, b.dueDate || b.due));
              return pendingTasks.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#a8adb7' }}><CheckCircle size={32} style={{ opacity: 0.4 }} /><p style={{ margin: '0.5rem 0 0' }}>All tasks completed!</p></div>
            ) : pendingTasks.slice(0, 5).map(task => {
              const due = task.dueDate || task.due;
              const taskIsOverdue = !task.completed && isOverdue({ dueDate: due, dueTime: task.dueTime, status: task.status });
              return (
                <div key={task.id} className="task-item">
                  <div className="task-details">
                    <div className="task-title" style={{ color: taskIsOverdue ? '#ef4444' : '#e8eaed' }}>{taskIsOverdue && '⚠ '}{task.title}</div>
                    <div className="task-meta"><span className="task-case">{task.caseName || task.case || ''}</span></div>
                  </div>
                  <div className="task-due" style={{ color: taskIsOverdue ? '#ef4444' : '#a8adb7', fontWeight: taskIsOverdue ? 600 : 400 }}>
                    {due ? formatAccraDate(due, { month: 'short', day: 'numeric' }, 'en-US') : ''}
                  </div>
                </div>
              );
            })})()}
          </div>
        </div>

        {/* Court Dates Widget */}
        <DashboardCourtDates firmId={firmId} setActiveTab={setActiveTab} userId={userId} dataVersion={dataVersion} upcomingTasks={upcomingTasks} />

        {/* Recent Cases + Quick Actions */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Recent Cases</h3>
            <button className="btn-ghost" onClick={() => setActiveTab && setActiveTab('cases')}>View All</button>
          </div>
          <div className="case-list">
            {recentCases.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#a8adb7' }}><Briefcase size={32} style={{ opacity: 0.4 }} /><p style={{ margin: '0.5rem 0 0' }}>No cases yet</p></div>
            ) : recentCases.slice(0, 5).map(caseItem => (
              <div key={caseItem.id} className="case-item">
                <div className="case-info">
                  <div className="case-name">{caseItem.name}</div>
                  <div className="case-meta"><span className="case-type">{caseItem.type}</span><span className="case-separator">•</span><span className="case-client">{caseItem.client}</span></div>
                </div>
                <div className="case-status-group">
                  <span className={`status-badge ${(caseItem.status || '').toLowerCase()}`}>{caseItem.status}</span>
                </div>
              </div>
            ))}
          </div>
         
        </div>

{/* Quick Actions */}
        <div className="dashboard-card">
          <div className="card-header"><h3>Quick Actions</h3></div>
          <div style={{ display: 'grid', gap: '0.6rem', padding: '0.5rem 0' }}>
            {[
              { label: 'New Case', icon: Plus, tab: 'cases', color: '#d4a853' },
              { label: 'Record Time', icon: Clock, tab: 'billing', color: '#10b981' },
              { label: 'Client Portal', icon: Users, tab: 'clients', color: '#f59e0b' },
              { label: 'Legal Research', icon: BookOpen, tab: 'legal-research', color: '#3b82f6' },
              { label: 'AI Assistant', icon: Sparkles, tab: 'ai-assistant', color: '#8b5cf6' },
              { label: 'Documents', icon: FileText, tab: 'documents', color: '#06b6d4' },
              { label: 'Team Hub', icon: Users, tab: 'team', color: '#ec4899' },
            ].map((action, i) => (
              <button key={i} onClick={() => setActiveTab && setActiveTab(action.tab)} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 1.25rem', background: action.color + '10', border: '1px solid ' + action.color + '25', borderRadius: '12px', color: '#e8eaed', cursor: 'pointer', fontSize: '0.95rem', fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.2s', textAlign: 'left', width: '100%' }} onMouseEnter={e => { e.currentTarget.style.background = action.color + '25'; e.currentTarget.style.borderColor = action.color; }} onMouseLeave={e => { e.currentTarget.style.background = action.color + '10'; e.currentTarget.style.borderColor = action.color + '25'; }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: action.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: action.color, flexShrink: 0 }}><action.icon size={18} /></div>
                {action.label}
              </button>
            ))}
          </div>
        </div>

        {/* Practice Area Distribution (REAL DATA) */}
        <div className="dashboard-card full-width">
          <div className="card-header">
            <h3>Practice Area Distribution</h3>
            <button className="btn-ghost" onClick={() => setActiveTab && setActiveTab('analytics')}>View Details</button>
          </div>
          {practiceAreaStats.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#a8adb7' }}>No cases yet — add cases to see distribution</div>
          ) : (
            <div className="practice-areas">
              {practiceAreaStats.map((area, index) => (
                <div key={index} className="practice-area-item">
                  <div className="practice-area-info">
                    <div className="practice-area-name">{area.area}</div>
                    <div className="practice-area-stats"><span>{area.cases} case{area.cases !== 1 ? 's' : ''}</span></div>
                  </div>
                  <div className="practice-area-bar">
                    <div className="practice-area-fill" style={{ width: `${area.percentage}%`, background: paColors[index % paColors.length] }}></div>
                  </div>
                  <div className="practice-area-percentage">{area.percentage}%</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── NEW CASE MODAL ── */}
        {showNewCaseModal && (
          <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowNewCaseModal(false); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
            <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '600px', background: '#0f1219', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', overflow: 'auto', maxHeight: '90vh' }}>
              <div style={{ padding: '1.25rem 2rem', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, color: '#e8eaed' }}>Create New Case</h2>
                <button className="btn-icon" onClick={() => setShowNewCaseModal(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleNewCaseSubmit}>
                <div style={{ padding: '1.5rem 2rem', display: 'grid', gap: '1rem' }}>
                  {newCaseError && <div style={{ padding: '0.6rem 1rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '0.85rem' }}>{newCaseError}</div>}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ color: '#a8adb7', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Case Name *</label>
                      <input value={newCaseForm.name} onChange={e => setNewCaseForm({ ...newCaseForm, name: e.target.value })} placeholder="Adu v. Republic" required style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.65rem', color: '#e8eaed', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ color: '#a8adb7', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Client Name *</label>
                      <input value={newCaseForm.client} onChange={e => setNewCaseForm({ ...newCaseForm, client: e.target.value })} placeholder="Kwame Adu" required style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.65rem', color: '#e8eaed', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ color: '#a8adb7', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Case Number</label>
                      <input value={newCaseForm.caseNumber} onChange={e => setNewCaseForm({ ...newCaseForm, caseNumber: e.target.value })} placeholder="Auto-generated if blank" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.65rem', color: '#e8eaed', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ color: '#a8adb7', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Practice Area *</label>
                      <select value={newCaseForm.type} onChange={e => setNewCaseForm({ ...newCaseForm, type: e.target.value })} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.65rem', color: '#e8eaed', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}>
                        {dashCaseTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ color: '#a8adb7', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Status</label>
                      <select value={newCaseForm.status} onChange={e => setNewCaseForm({ ...newCaseForm, status: e.target.value })} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.65rem', color: '#e8eaed', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}>
                        <option value="Pending">Intake / Pending</option><option value="Active">Active</option><option value="Pleadings">Pleadings</option><option value="CMC">Case Management Conference</option><option value="Trial">Trial / Hearing</option><option value="On Hold">On Hold</option><option value="Closed">Closed</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ color: '#a8adb7', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Priority</label>
                      <select value={newCaseForm.priority} onChange={e => setNewCaseForm({ ...newCaseForm, priority: e.target.value })} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.65rem', color: '#e8eaed', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}>
                        <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={{ color: '#a8adb7', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Description</label>
                    <textarea value={newCaseForm.description} onChange={e => setNewCaseForm({ ...newCaseForm, description: e.target.value })} placeholder="Brief case description..." rows={2} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.65rem', color: '#e8eaed', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit', resize: 'vertical' }} />
                  </div>

                  {/* Parties */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                      <label style={{ color: '#a8adb7', fontSize: '0.8rem' }}>👥 Parties</label>
                      <button type="button" onClick={() => setDashParties(prev => [...prev, { name: '', role: 'Defendant', isOurClient: false }])} style={{ background: 'rgba(212,168,83,0.12)', border: '1px solid rgba(212,168,83,0.3)', borderRadius: '6px', padding: '0.25rem 0.5rem', color: '#d4a853', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, fontFamily: 'inherit' }}>+ Add Party</button>
                    </div>
                    {dashParties.length === 0 ? (
                      <div style={{ padding: '0.75rem', textAlign: 'center', color: '#6b7280', fontSize: '0.8rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px dashed rgba(255,255,255,0.08)' }}>Optional — add parties after creation via the Edit tab</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                        {dashParties.map((p, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                            <input value={p.name} onChange={e => setDashParties(prev => prev.map((pp, i) => i === idx ? { ...pp, name: e.target.value } : pp))} placeholder="Name" style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '0.5rem', color: '#e8eaed', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit' }} />
                            <select value={p.role} onChange={e => setDashParties(prev => prev.map((pp, i) => i === idx ? { ...pp, role: e.target.value } : pp))} style={{ background: '#0f1219', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.5rem', color: '#e8eaed', fontSize: '0.82rem', fontFamily: 'inherit', cursor: 'pointer', width: '130px' }}>{PARTY_ROLES.map(r => <option key={r} value={r}>{r}</option>)}</select>
                            <button type="button" onClick={() => setDashParties(prev => prev.map((pp, i) => i === idx ? { ...pp, isOurClient: !pp.isOurClient } : pp))} style={{ background: p.isOurClient ? 'linear-gradient(135deg, #d4a853, #b8862d)' : 'rgba(255,255,255,0.08)', border: p.isOurClient ? 'none' : '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.35rem 0.5rem', color: p.isOurClient ? 'white' : '#6b7280', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>{p.isOurClient ? '★' : '☆'}</button>
                            <button type="button" onClick={() => setDashParties(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.2rem', opacity: 0.6 }}><Trash2 size={13} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Playbook */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={{ color: '#a8adb7', fontSize: '0.8rem', marginBottom: '0.3rem' }}>⚖️ Procedural Playbook</label>
                    <select value={newCaseForm.casePlaybook} onChange={e => setNewCaseForm({ ...newCaseForm, casePlaybook: e.target.value })} style={{ background: '#0f1219', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.65rem', color: '#e8eaed', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}>
                      <option value="">None — assign later</option>
                      {Object.entries(CASE_PLAYBOOKS).map(([key, pb]) => <option key={key} value={key}>{pb.icon} {pb.name} ({pb.law})</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ padding: '1rem 2rem', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                  <button type="button" className="btn-secondary" onClick={() => setShowNewCaseModal(false)} disabled={newCaseLoading}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={newCaseLoading}>{newCaseLoading ? 'Creating...' : 'Create Case'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default Dashboard;
